import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ICertificadoEntidad, IQueryParam } from 'modelos/src';
import { Subscription } from 'rxjs';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';

import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { CertificadoEntidadService } from '../../../../auxiliares/servicios/http/certificado-entidad.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import {
  CrearEditarCertificadoEntidadComponent,
  IParamCertificadoEntidad,
} from '../crear-editar-certificado-entidad/crear-editar-certificado-entidad.component';
import { ParamsService } from '../../../../auxiliares/servicios/params.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { VerCertificadoEntidadComponent } from '../ver-certificado-entidad/ver-certificado-entidad.component';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import {
  estadoHtml,
  fechaCelda,
} from '../../../../auxiliares/tabla/celdas';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { IEstadoCelda } from '../../../../auxiliares/tabla/tabla.interfaces';

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-certificado-entidad',
  templateUrl: './listado-certificado-entidad.component.html',
  styleUrls: ['./listado-certificado-entidad.component.scss'],
})
export class ListadoCertificadoEntidadComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private dialogService = inject(DialogService);
  private service = inject(CertificadoEntidadService);
  private formularios = inject(FormularioDialogService);
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private paramsService = inject(ParamsService);

  //// Tabla nueva
  public name = 'ListadoCertificadoEntidadComponent';
  public datos?: ICertificadoEntidad[];
  public columnas?: IColumnas<ICertificadoEntidad>[];
  public totalCount = 0;
  public botonCrear: ICrearBoton = {
    mostrar: true,
    tooltip: 'Iniciar certificado',
    color: 'primary',
    icon: 'add',
    accion: () => this.crear(),
    tipo: 'material',
  };
  public search: IRegExpSearch = {
    fields: ['_id'],
  };

  public filtros: IFiltroTabla[] = [];

  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };

  public populate = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'tracker',
      select: 'identificacion',
    },
    {
      path: 'activo',
      select: 'identificacion',
    },
    {
      path: 'alarma',
      select: 'nombre',
    },
  ];

  public filter: any = {};
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    limit: +this.helper.getPageSize(this.name),
    page: 0,
    populate: JSON.stringify(this.populate),
  };

  @Input() public params?: IParamCertificadoEntidad;

  // Listado Continuo
  public datos$?: Subscription;

  // ACCIONES
  public async crear(): Promise<void> {
    // Validar que no exista un certificado activo (sin fechaEmision)
    const certificadoActivo = this.datos?.find((cert) => !cert.fechaEmision);
    if (certificadoActivo) {
      this.helper.notifError(
        'Ya existe un certificado activo sin finalizar. Tenés que finalizarlo antes de iniciar uno nuevo.',
      );
      return;
    }

    let res;
    if (this.params.crear === 'Dialog') {
      this.paramsService.setParams({ dialog: true, params: this.params });
      res = this.formularios.abrir(CrearEditarCertificadoEntidadComponent, {
        tamano: 'mediano',
      });
    } else {
      this.paramsService.setParams({ dialog: false, params: this.params });
      const extras: NavigationExtras = {
        relativeTo: this.route,
      };
      this.router.navigate(['crear'], extras);
    }
    if (res) {
      res.afterClosed().subscribe(async (data: any) => {
        if (data) {
          await this.listar();
        }
      });
    }
  }

  public async editar(data: ICertificadoEntidad) {
    let res;
    if (this.params.crear === 'Dialog') {
      this.paramsService.setParams({
        dialog: true,
        params: this.params,
        edit: data,
      });
      res = this.formularios.abrir(CrearEditarCertificadoEntidadComponent, {
        tamano: 'mediano',
      });
    } else {
      this.paramsService.setParams({
        dialog: false,
        params: this.params,
        edit: data,
      });
      const extras: NavigationExtras = {
        relativeTo: this.route,
      };
      this.router.navigate(['editar', data._id], extras);
    }
    if (res) {
      res.afterClosed().subscribe(async (r: any) => {
        if (r) {
          await this.listar();
        }
      });
    }
  }

  public async eliminar(dato: ICertificadoEntidad): Promise<void> {
    const confirm = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Eliminar el certificado?`,
    );
    if (confirm) {
      try {
        await this.service.delete(dato._id!);
        this.helper.notifSuccess('Eliminación correcta');
        await this.listar();
      } catch (error) {
        this.helper.notifError(error);
      }
    }
  }

  public async verCertificado(dato: ICertificadoEntidad): Promise<void> {
    this.formularios.abrir(VerCertificadoEntidadComponent, {
      tamano: 'grande',
      data: { certificado: dato },
      cerrarAfuera: true,
    });
  }

  public async finalizarAhora(dato: ICertificadoEntidad): Promise<void> {
    const confirm = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Finalizar el certificado ahora?`,
    );
    if (confirm) {
      try {
        await this.service.finalizarAhora(dato._id!);
        this.helper.notifSuccess('Certificado finalizado correctamente');
        await this.listar();
      } catch (error) {
        this.helper.notifError(error);
      }
    }
  }

  // Listar

  public async actualizar(): Promise<void> {
    await this.listar();
  }

  public async listar(): Promise<void> {
    const filter = this.query.filter ? JSON.parse(this.query.filter) : {};

    if (this.params?.idEntidad) {
      filter['idEntidad'] = this.params.idEntidad;
    }
    this.query.filter = JSON.stringify(filter);

    this.datos$?.unsubscribe();
    this.datos$ = this.lista2
      .certificadosEntidades(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.datos = data.datos;
      });
  }

  /**
   * Estado del certificado deducido de sus fechas: sin fecha de emisión o
   * con la emisión a futuro sigue en curso; con la emisión pasada, emitido.
   */
  private estadoCertificado(dato: ICertificadoEntidad): IEstadoCelda {
    const emision = dato.fechaEmision ? new Date(dato.fechaEmision) : null;
    if (emision && emision.getTime() <= Date.now()) {
      return { texto: 'Emitido', tono: 'ok', icono: 'check_circle' };
    }
    return { texto: 'En curso', tono: 'info', icono: 'pending' };
  }

  private setColumnas() {
    const columnas: IColumnas<ICertificadoEntidad>[] = [
      // Fecha inicio
      {
        header: {
          label: 'Fecha inicio',
          sort: 'fechaComienzo',
          sortable: true,
        },
        row: {
          fecha: (dato) => dato.fechaComienzo,
          noData: 'Sin fecha',
        },
      },
      // Fecha emisión, con el estado del certificado debajo
      {
        header: {
          label: 'Fecha emisión',
          sort: 'fechaEmision',
          sortable: true,
        },
        row: {
          html: (dato) => {
            const fecha = dato.fechaEmision
              ? `<span class="tabla-fecha">${escaparHtml(fechaCelda(dato.fechaEmision))}</span>`
              : `<span class="no-info">Sin fecha</span>`;
            return `<div class="tabla-celda-col">${fecha}<span>${estadoHtml(this.estadoCertificado(dato))}</span></div>`;
          },
        },
      },
    ];

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver certificado',
            click: (dato) => this.verCertificado(dato),
            disabled: (dato) => {
              // Deshabilitar si la fecha actual es menor a la fecha de emisión
              if (dato.fechaEmision) {
                const fechaEmision = new Date(dato.fechaEmision);
                const fechaActual = new Date();
                return fechaActual < fechaEmision;
              }
              return false;
            },
          },
          {
            tipo: 'material',
            icon: 'task_alt',
            tooltip: 'Finalizar ahora',
            click: (dato) => this.finalizarAhora(dato),
            disabled: (dato) => {
              const fechaActual = new Date();

              // Debe tener ambas fechas para poder evaluar
              if (!dato.fechaComienzo || !dato.fechaEmision) return true;

              const fechaComienzo = new Date(dato.fechaComienzo);
              const fechaEmision = new Date(dato.fechaEmision);

              // Habilitar solo si la fecha actual está ENTRE fechaComienzo y fechaEmision
              const estaEnRango =
                fechaActual >= fechaComienzo && fechaActual <= fechaEmision;

              // Retornar true para deshabilitar, false para habilitar
              return !estaEnRango;
            },
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () => false,
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  private setearFiltros() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
  }

  async ngOnInit(): Promise<void> {
    this.setColumnas();
    this.setearFiltros();
    if (this.params) {
      // Si se recibe el parametro desde el componente padre
      if (this.params.botonCrear) this.botonCrear.mostrar = true;
    } else {
      // Si se recibe el parametro desde la URL
      this.route.queryParams.subscribe((params) => {
        this.params = { ...this.params, ...(params as any) };
      });
      this.botonCrear.mostrar = true;
    }
    await this.listar();
  }

  ngOnDestroy(): void {
    this.datos$?.unsubscribe();
  }
}
